import SectionCard from '../../components/ui/SectionCard'
import EmptyState from '../../components/ui/EmptyState'
import LoadingState from '../../components/ui/LoadingState'
import ErrorState from '../../components/ui/ErrorState'
import ExchangeRateHealthBadge from './ExchangeRateHealthBadge'
import ExchangeRateCellDetails from './ExchangeRateCellDetails'
import ExchangeRatePaginationControls from './ExchangeRatePaginationControls'

function formatRate(cell) {
  if (!cell) return '—'
  if (cell.baseCurrency === cell.quoteCurrency) return '1.0000'
  if (cell.rateDecimal === null || cell.rateDecimal === undefined || cell.rateDecimal === '') return '—'
  return String(cell.rateDecimal)
}

export default function ExchangeRatesMatrix({
  matrix,
  loading,
  error,
  onRetry,
  selectedCell,
  onCellSelect,
  sort,
  onSortChange,
  onPageChange,
  onPageSizeChange,
}) {
  if (loading && !matrix) {
    return <LoadingState text="Loading matrix..." />
  }

  if (error && !matrix) {
    return <ErrorState text={error} action={<button type="button" className="underline" onClick={onRetry}>Retry</button>} />
  }

  const baseCurrencies = matrix?.baseCurrencies || []
  const quoteCurrencies = matrix?.quoteCurrencies || []
  const cells = Array.isArray(matrix?.cells) ? matrix.cells : []

  if (baseCurrencies.length === 0 || quoteCurrencies.length === 0) {
    return (
      <EmptyState
        title="No matrix data"
        description="Apply filters or run a sync to populate matrix cells."
      />
    )
  }

  const cellMap = new Map(cells.map((cell) => [`${cell.baseCurrency}|${cell.quoteCurrency}`, cell]))

  return (
    <SectionCard title="Matrix" subtitle="Rows are base currencies, columns are quote currencies.">
      <div className="overflow-x-auto rounded-xl border border-outline bg-surface">
        <table className="min-w-full border-collapse text-sm" aria-label="Exchange rate matrix">
          <thead className="sticky top-0 z-10 bg-table-header">
            <tr>
              <th
                aria-sort={sort?.key === 'baseCurrency' ? (sort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
                className="sticky left-0 z-20 border-b border-outline bg-table-header px-3 py-2 text-left"
              >
                <button type="button" className="font-semibold" onClick={() => onSortChange('baseCurrency')}>
                  Base / Quote
                </button>
              </th>
              {quoteCurrencies.map((quote) => (
                <th key={quote} className="border-b border-outline px-3 py-2 text-left">{quote}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {baseCurrencies.map((base) => (
              <tr key={base}>
                <th className="sticky left-0 z-10 border-b border-outline bg-surface-elevated px-3 py-2 text-left">{base}</th>
                {quoteCurrencies.map((quote) => {
                  const key = `${base}|${quote}`
                  const cell = cellMap.get(key)

                  return (
                    <td key={key} className="border-b border-outline px-3 py-2 align-top">
                      <button
                        type="button"
                        onClick={() => onCellSelect(cell || {
                          baseCurrency: base,
                          quoteCurrency: quote,
                          rateDecimal: null,
                          rateDate: null,
                          source: null,
                          sourcePriority: null,
                          fetchedAt: null,
                          status: 'missing',
                          freshness: 'missing',
                          isDerived: false,
                          derivedVia: null,
                          validFrom: null,
                          validTo: null,
                        })}
                        className="w-full rounded-md border border-transparent p-2 text-left hover:border-outline hover:bg-surface-elevated"
                      >
                        <div className="font-medium text-foreground">{formatRate(cell)}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {cell?.freshness ? <ExchangeRateHealthBadge health={cell.freshness} /> : null}
                          {cell?.isDerived ? <ExchangeRateHealthBadge health="derived" label="Derived" /> : null}
                        </div>
                      </button>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
        <ExchangeRatePaginationControls
          page={matrix?.page}
          pageSize={matrix?.pageSize}
          total={matrix?.total}
          totalPages={matrix?.totalPages}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          itemLabel="base rows"
        />
      </div>

      <ExchangeRateCellDetails cell={selectedCell} />
    </SectionCard>
  )
}
